import { IContact } from "@/interfaces/Icontact";
import { baseApi } from "./api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const CONTACTS_ROUTES = {
  GET_CONTACTS: "/contacts/",
  EDIT_CONTACT: "/contacts/:id",
  GET_CONTACT_PICTURE: "/media/contact/picture",
  UPDATE_CONTACT_PICTURE: "/contacts/:id/picture",
  BLOCK_CONTACT: "/contacts/:id/block",
  UNBLOCK_CONTACT: "/contacts/:id/unblock",
  ADD_PHONE_TO_CONTACT: "/contacts/{contact_id}/phones",
  UPDATE_PHONE_OF_CONTACT: "/contacts/{contact_id}/phones/{id}",
  DELETE_PHONE_FROM_CONTACT: "/contacts/{contact_id}/phones/{id}",
};

const contactsApi = {
  blockContact: async (id: string) => {
    const response = await baseApi.post(
      CONTACTS_ROUTES.BLOCK_CONTACT.replace(":id", id)
    );
    return response;
  },
  unblockContact: async (id: string) => {
    const response = await baseApi.post(
      CONTACTS_ROUTES.UNBLOCK_CONTACT.replace(":id", id)
    );
    return response;
  },
  addPhoneToContact: async (contact_id: string, phone: string) => {
    const response = await baseApi.post(
      CONTACTS_ROUTES.ADD_PHONE_TO_CONTACT.replace("{contact_id}", contact_id),
      { phone }
    );
    return response;
  },
  updatePhoneOfContact: async (
    contact_id: string,
    id: string,
    phone: string
  ) => {
    const response = await baseApi.put(
      CONTACTS_ROUTES.UPDATE_PHONE_OF_CONTACT.replace(
        "{contact_id}",
        contact_id
      ).replace("{id}", id),
      {
        phone,
      }
    );
    return response;
  },

  deletePhoneFromContact: async (contact_id: string, id: string) => {
    const response = await baseApi.delete(
      CONTACTS_ROUTES.DELETE_PHONE_FROM_CONTACT.replace(
        "{contact_id}",
        contact_id
      ).replace("{id}", id)
    );
    return response;
  },

  getContacts: async () => {
    try {
      const response = await baseApi.get(CONTACTS_ROUTES.GET_CONTACTS);
      return response;
    } catch (error) {
      console.error("Error in getContacts:", error);
      throw error;
    }
  },

  editContact: async (id: string, contact: IContact) => {
    try {
      const response = await baseApi.put(
        `${CONTACTS_ROUTES.EDIT_CONTACT.replace(":id", id)}`,
        contact
      );
      return response;
    } catch (error) {
      console.error("Error in editContact:", error);
      throw error;
    }
  },

  addContact: async (contact: IContact) => {
    try {
      const response = await baseApi.post(
        CONTACTS_ROUTES.GET_CONTACTS,
        contact
      );
      return response;
    } catch (error) {
      console.error("Error in addContact:", error);
      throw error;
    }
  },

  addUpdateContactPicture: async (id: string, file: File) => {
    const formData = new FormData();
    formData.append("picture_file", file);

    const response = await baseApi.put(
      `${CONTACTS_ROUTES.EDIT_CONTACT.replace(":id", id)}/picture`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  },

  getContactPicture: async (picture_url: string) => {
    console.log("picture_url", picture_url);

    try {
      const response = await baseApi.get(picture_url, {
        responseType: "blob",
      });
      return response;
    } catch (error) {
      console.error("Error in getContactPicture:", error);
      throw error;
    }
  },

  toggleContactVerification: async (id: string, contact: IContact) => {
    try {
      const response = await baseApi.put(
        `${CONTACTS_ROUTES.EDIT_CONTACT.replace(":id", id)}`,
        {
          ...contact,
          is_verified: !contact.is_verified,
        }
      );
      return response;
    } catch (error) {
      console.error("Error in toggleContactVerification:", error);
      throw error;
    }
  },
  deleteContact: async (id: string) => {
    try {
      const response = await baseApi.delete(
        `${CONTACTS_ROUTES.EDIT_CONTACT.replace(":id", id)}`
      );
      return response;
    } catch (error) {
      console.error("Error in deleteContact:", error);
      throw error;
    }
  },
};

// React Query hooks
export function useContacts() {
  return useQuery({
    queryKey: ["contacts"],
    queryFn: contactsApi.getContacts,
  });
}

export function useAddContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: contactsApi.addContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useEditContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, contact }: { id: string; contact: IContact }) =>
      contactsApi.editContact(id, contact),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: contactsApi.deleteContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useToggleContactVerification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, contact }: { id: string; contact: IContact }) =>
      contactsApi.toggleContactVerification(id, contact),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useAddPhoneToContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      contact_id,
      phone,
    }: {
      contact_id: string;
      phone: string;
    }) => contactsApi.addPhoneToContact(contact_id, phone),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useUpdatePhoneOfContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      contact_id,
      id,
      phone,
    }: {
      contact_id: string;
      id: string;
      phone: string;
    }) => contactsApi.updatePhoneOfContact(contact_id, id, phone),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useDeletePhoneFromContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contact_id, id }: { contact_id: string; id: string }) =>
      contactsApi.deletePhoneFromContact(contact_id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useBlockContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactsApi.blockContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useUnblockContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactsApi.unblockContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export { contactsApi };
